import { listEstablishmentsQueryOptions } from "@packages/api-client/hooks/establishmentController";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import {
	CheckCircle,
	CloudOff,
	Database,
	HardDrive,
	RefreshCw,
	WifiOff,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useSyncStatus } from "@/context/SyncStatusContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import type { DatasetDef, DatasetScope } from "@/lib/offline/datasetRegistry";
import { DATASET_REGISTRY } from "@/lib/offline/datasetRegistry";
import {
	getLatestSyncAtForDataset,
	getPreparedScopes,
	subscribeDatasetMeta,
} from "@/lib/offline/offlineMeta";
import { scopeKey } from "@/lib/offline/prepareDataset";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/offline-center/",
)({
	loader: async ({ context, params }) => {
		context.queryClient.ensureQueryData(
			listEstablishmentsQueryOptions(params.orgId),
		);
	},
	component: OfflineCenterPage,
});

function OfflineCenterPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({ from: "/_auth/app/_app/org/$orgId" });
	const { online, queueDepth, storageEstimate } = useSyncStatus();

	const { data: establishmentsData } = useSuspenseQuery(
		listEstablishmentsQueryOptions(orgId),
	);
	const establishments = establishmentsData?.data ?? [];

	const storageUsedMb = storageEstimate
		? (storageEstimate.usage / (1024 * 1024)).toFixed(2)
		: "—";
	const storageQuotaMb = storageEstimate
		? (storageEstimate.quota / (1024 * 1024)).toFixed(0)
		: "—";

	return (
		<div className="max-w-lg mx-auto px-4 py-6 flex flex-col gap-4">
			<h1 className="text-2xl font-bold">{t("offline.center.title")}</h1>

			{/* Connectivity */}
			<div className="card bg-base-200 p-4 flex flex-col gap-2">
				<div className="flex items-center gap-2 font-semibold">
					<WifiOff className="h-4 w-4" />
					<span>{t("offline.center.connectivity.title")}</span>
				</div>
				<div className="flex items-center gap-2">
					<span
						className={`inline-block h-2.5 w-2.5 rounded-full ${online ? "bg-success" : "bg-error"}`}
					/>
					<span className="text-sm">
						{online
							? t("offline.center.connectivity.online")
							: t("offline.center.connectivity.offline")}
					</span>
				</div>
			</div>

			{/* Pending Queue */}
			<div className="card bg-base-200 p-4 flex flex-col gap-2">
				<div className="flex items-center gap-2 font-semibold">
					<RefreshCw className="h-4 w-4" />
					<span>{t("offline.center.queue.title")}</span>
				</div>
				<p className="text-sm">
					{queueDepth > 0
						? t("offline.status.queued", { count: queueDepth })
						: t("offline.queue.empty")}
				</p>
			</div>

			{/* Datasets */}
			<div className="card bg-base-200 p-4 flex flex-col gap-3">
				<div className="flex items-center gap-2 font-semibold">
					<Database className="h-4 w-4" />
					<span>{t("offline.center.datasets.title")}</span>
				</div>

				<div className="flex flex-col gap-2">
					{Object.values(DATASET_REGISTRY).map((def) => (
						<DatasetRow
							key={`${orgId}:${def.id}`}
							def={def}
							orgId={orgId}
							establishments={establishments}
						/>
					))}
				</div>
			</div>

			{/* Storage */}
			<div className="card bg-base-200 p-4 flex flex-col gap-2">
				<div className="flex items-center gap-2 font-semibold">
					<HardDrive className="h-4 w-4" />
					<span>{t("offline.center.storage.title")}</span>
				</div>
				<p className="text-sm">
					{storageEstimate
						? t("offline.center.storage.usage", {
								used: `${storageUsedMb} MB`,
								quota: `${storageQuotaMb} MB`,
							})
						: "—"}
				</p>
				{storageEstimate && (
					<div className="w-full bg-base-300 rounded-full h-1.5">
						<div
							className="bg-primary h-1.5 rounded-full"
							style={{
								width: `${Math.min(100, (storageEstimate.usage / storageEstimate.quota) * 100).toFixed(1)}%`,
							}}
						/>
					</div>
				)}
			</div>

			{/* App Version */}
			<div className="card bg-base-200 p-4 flex flex-col gap-2">
				<div className="flex items-center gap-2 font-semibold">
					<CloudOff className="h-4 w-4" />
					<span>{t("offline.center.appVersion.title")}</span>
				</div>
				<p className="text-sm font-mono text-base-content/70">
					{import.meta.env.VITE_APP_VERSION ?? "dev"}
				</p>
			</div>
		</div>
	);
}

/** One registry dataset: last-synced status + a Prepare/Refresh action. */
function DatasetRow({
	def,
	orgId,
	establishments,
}: {
	def: DatasetDef;
	orgId: string;
	establishments: Array<{ id: string; name: string }>;
}) {
	const { t } = useTranslation();
	const { formatRelativeTime } = useDateFormatter();
	const { online, prepareAll } = useSyncStatus();

	const [preparing, setPreparing] = useState(false);
	const [lastPrepareResult, setLastPrepareResult] = useState<{
		ok: boolean;
		error?: string;
	} | null>(null);
	const [lastPreparedAt, setLastPreparedAt] = useState<number | null>(null);
	const [preparedScopes, setPreparedScopes] = useState<Set<string>>(
		() => new Set(),
	);

	// Read lastPreparedAt from IndexedDB so the status survives a page refresh,
	// and re-read it when a background re-prepare writes new meta.
	useEffect(() => {
		let active = true;
		const read = () => {
			Promise.all([
				getLatestSyncAtForDataset(def.id, orgId),
				getPreparedScopes(def.id, orgId),
			])
				.then(([ts, scopes]) => {
					if (!active) return;
					setLastPreparedAt(ts);
					setPreparedScopes(scopes);
				})
				.catch(() => {});
		};
		read();
		const unsubscribe = subscribeDatasetMeta(read);
		return () => {
			active = false;
			unsubscribe();
		};
	}, [def.id, orgId]);

	// Org-wide datasets have no farm dimension — prepare them once under the
	// org scope instead of refetching the identical list per establishment.
	const scopes = def.orgScoped
		? [{ organizationId: orgId }]
		: [
				{ organizationId: orgId },
				...establishments.map((e) => ({
					organizationId: orgId,
					establishmentId: e.id,
				})),
			];
	const scopeLabel = (scope: DatasetScope) =>
		establishments.find((e) => e.id === scope.establishmentId)?.name ??
		t("offline.center.datasets.scope.allFarms");
	const missingScopes = scopes.filter((s) => !preparedScopes.has(scopeKey(s)));

	// Prepare/Refresh always covers every scope in the org — the org-wide
	// ("All Farms") scope plus each establishment — regardless of which farm is
	// currently selected up top, so switching farms offline always works and a
	// single click keeps the whole org available offline.
	const handlePrepare = async () => {
		setPreparing(true);
		setLastPrepareResult(null);
		try {
			const result = await prepareAll(def.id, scopes);
			setLastPreparedAt(
				result.failures.length < scopes.length ? Date.now() : null,
			);
			if (result.ok) {
				setLastPrepareResult({ ok: true });
			} else {
				const names = result.failures
					.map((f) => {
						const label = scopeLabel(f.scope);
						return f.overCap
							? `${label} (${t("offline.center.datasets.overCap")})`
							: label;
					})
					.join(", ");
				setLastPrepareResult({
					ok: false,
					error: t("offline.center.prepareAllPartial", {
						failed: result.failures.length,
						total: scopes.length,
						names,
					}),
				});
			}
		} finally {
			setPreparing(false);
		}
	};

	return (
		<div className="flex items-center justify-between">
			<div>
				<p className="text-sm font-medium">{t(def.labelKey)}</p>
				{lastPreparedAt ? (
					<p className="text-xs text-base-content/60">
						{t("offline.center.datasets.lastSynced", {
							time: formatRelativeTime(lastPreparedAt),
						})}
					</p>
				) : (
					<p className="text-xs text-base-content/60">
						{t("offline.center.datasets.neverSynced")}
					</p>
				)}
				{lastPrepareResult?.error && (
					<p className="text-xs text-error mt-0.5">{lastPrepareResult.error}</p>
				)}
				{lastPreparedAt &&
					!lastPrepareResult?.error &&
					!preparing &&
					missingScopes.length > 0 && (
						<p className="text-xs text-warning mt-0.5">
							{t("offline.center.datasets.notYetAvailable", {
								names: missingScopes.map(scopeLabel).join(", "),
							})}
						</p>
					)}
				{lastPreparedAt &&
					missingScopes.length === 0 &&
					!lastPrepareResult?.error && (
						<p className="text-xs text-success mt-0.5 flex items-center gap-1">
							<CheckCircle className="h-3 w-3" />
							{t("offline.center.datasets.availableOffline")}
						</p>
					)}
			</div>
			<Button
				size="sm"
				variant="outline"
				onClick={handlePrepare}
				disabled={preparing || !online}
			>
				{preparing ? (
					<RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" />
				) : null}
				{lastPreparedAt
					? t("offline.center.datasets.refresh")
					: t("offline.center.datasets.prepare")}
			</Button>
		</div>
	);
}
